import { createRouter, createWebHistory } from 'vue-router'
import lspRoutes from './lsp.routes'
import authRoutes from './auth.routes'
import { getUser, logout } from '@services/authentication'
import AppLayout from '../layouts/AppLayout.vue'

type User = {
  email: string
  lastName: string
  firstName: string
  status: string
  dob: string
  nationality: string
  sex: string
  account_id: string
}
const metaFactory = (auth: boolean, title: string, area: string, gate?: (user: User) => boolean) => ({
  auth,
  title,
  gate,
  area,
})
const routes = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' },
  },
  {
    name: 'general',
    path: '/',
    component: AppLayout,
    meta: metaFactory(true, 'N/A', 'general'),
    redirect: { name: 'services' },
    children: [
      {
        name: 'FAQ',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        name: 'services',
        path: 'services',
        component: () => import('../pages/services/Services.vue'),
      },
      {
        name: 'TOC',
        path: 'general/terms-conditions-privacy',
        component: () => import('../pages/termsConditionsAndPolicies/TermsConditionsAndPolicies.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
    ],
  },
  lspRoutes,
  authRoutes,
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(function (to, from, next) {
  const user = getUser(false) // get user from local storage
  const isLoggedIn = !!user // if user is null then user needs to login

  // exceed expiration date
  if (isLoggedIn && Date.now() >= user.exp * 1000) {
    logout()
  }

  if (from.name == 'LSP-FA' || from.name == 'LSP-FI') window.onbeforeunload = null

  // if where the user is heading requires login
  if (to.meta.auth) {
    if (isLoggedIn) {
      // if the user is logged in
      if (to.name == 'verify-code' && user!.status == 'INA') {
        return next()
      } else if (user!.status == 'INA') {
        return next({ name: 'verify-code' })
      } else {
        // check if gate is defined for the current route, if it is then test the user against it.
        // Continue if the gate returns true
        if (typeof to.meta.gate == 'function' && to.meta.gate(user)) {
          alert('Unknown!!')
          return next()
        } else if (typeof to.meta.gate == 'function') {
          // means that gate is a function but when called in the preceding if, it fails.
          return next('/')
        } else if (typeof to.meta.gate == 'undefined') {
          // if no gate is defined then proceed, means that only login is needed for the route to be accessed.

          // for lsp, only allow countries that are not belize
          if (to.meta.area == 'long_stay_permit' && user?.nationality == 'BLZ') {
            return next({
              name: 'services',
              query: { message: 'user does not qualify for Long Stay Permit Service' },
            })
          }
          return next()
        }
      }
    } else
      return next({
        name: 'login',
        query: {
          to: to.path,
        },
      }) // if the user is not logged in
  } else {
    if (isLoggedIn && to.name != 'logout' && to.name != 'verify-code') {
      // for routes that don't require login, don't show them unless the user is not logged in.
      return next({ name: 'services' })
    }

    return next()
  }
})

export default router
